import {
    Typography,
    FormControlLabel,
    Switch,
    Container,
    Box,
    FormGroup,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import React, { useMemo } from "react";
import theme from "Theme/AppTheme";
import SourceField from "./SourceField";
import AcxButton from "components/UI/AcxButton";
import { DataSourcesStore } from "../stores/DataSourcesStore";
import { observer } from "mobx-react";
import DataSourceContainer from "../components/DataSourceContainer";
import ConditionalLogic from "./ConditionalLogic/ConditionalLogic";
import { SourceFieldType } from "../types";
import ConcatFields from "./ConcatFields";
import ErrorHandler from "../components/ErrorHandler";
import { emptySourceField } from "../helpers/emptyStates";
import { isMeta1_5 } from "../helpers";

type SourceConfigurationProps = {
    store: DataSourcesStore;
};

const SourceConfiguration: React.FC<SourceConfigurationProps> = observer(
    ({ store }) => {
        const {
            sourceFields,
            setSourceFields,
            setShouldConcatenateFields,
            setShouldApplyConditions,
            shouldApplyConditions,
            shouldConcatenateFields,
            updateSelectedTargetFields,
            hasError,
            checkIfSourceConfigIsValid,
        } = store;

        // Exclude aliases from core fields: 3, 4, 8, 9, 10
        const duplicateAliases = useMemo(() => {
            const aliases = sourceFields.map((field) => field.alias);
            return aliases.filter(
                (alias, index) =>
                    aliases.indexOf(alias) !== index && !isMeta1_5(alias),
            );
        }, [sourceFields]);

        const hasSourceFieldsError =
            hasError.sourceFieldsMissing || hasError.sourceFieldsDuplicate;

        const handleAddField = () => {
            const updatedSourceFields = [...sourceFields, emptySourceField()];
            setSourceFields(updatedSourceFields);
        };

        const handleRemoveField = (id: string) => {
            const updatedSourceFields = sourceFields.filter(
                (field) => field.id !== id,
            );
            setSourceFields(updatedSourceFields);
            updateSelectedTargetFields({ sourceFieldId: id, action: "remove" });

            if (hasSourceFieldsError) {
                checkIfSourceConfigIsValid();
            }
        };

        const handleChange = (id: string, field: SourceFieldType) => {
            const updatedSourceFields = sourceFields.map((f) =>
                f.id === id ? field : f,
            );
            setSourceFields(updatedSourceFields);

            if (hasSourceFieldsError) {
                checkIfSourceConfigIsValid();
            }
        };

        return (
            <>
                <DataSourceContainer heading="Configure Source Fields">
                    <ErrorHandler
                        hasError={hasSourceFieldsError}
                        errorMessage={`${
                            hasError.sourceFieldsMissing
                                ? "Please enter a Source Field Alias and Path to continue."
                                : ""
                        }
                        ${
                            hasError.sourceFieldsDuplicate
                                ? "Please ensure each Source Field Alias is unique."
                                : ""
                        }`}
                    />
                    <Box
                        sx={{
                            padding: theme.spacing(2),
                            background: theme.palette.gray[100],
                            borderRadius: 2,
                        }}
                    >
                        {!!sourceFields.length && (
                            <>
                                <Box
                                    sx={{
                                        display: "flex",
                                        marginBottom: theme.spacing(2),
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: "14px",
                                            fontWeight: 600,
                                            marginRight: theme.spacing(2),
                                        }}
                                    >
                                        Req
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: "14px",
                                            fontWeight: 600,
                                            marginRight: theme.spacing(1),
                                        }}
                                    >
                                        Source Fields:
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: "10px",
                                            fontWeight: 600,
                                            paddingLeft: theme.spacing(1),
                                            paddingRight: theme.spacing(1),
                                            background: theme.palette.gray[300],
                                            borderRadius: "25px",
                                            display: "flex",
                                            alignItems: "center",
                                        }}
                                    >
                                        {sourceFields.length}
                                    </Typography>
                                </Box>
                                <Box
                                    component="ul"
                                    sx={{
                                        display: "flex",
                                        listStyle: "none",
                                        flexDirection: "column",
                                        paddingInlineStart: 0,
                                        gap: theme.spacing(2),
                                    }}
                                >
                                    {sourceFields.map((field) => (
                                        <SourceField
                                            key={field.id}
                                            hasDuplicateError={
                                                hasError.sourceFieldsDuplicate &&
                                                duplicateAliases.includes(
                                                    field.alias,
                                                )
                                            }
                                            hasMissingError={
                                                hasError.sourceFieldsMissing
                                            }
                                            targetFieldName={
                                                field.targetFieldName
                                            }
                                            id={field.id}
                                            path={field.path}
                                            alias={field.alias}
                                            isRequired={field.isRequired}
                                            extractionPattern={
                                                field.extractionPattern
                                            }
                                            onRemove={() =>
                                                handleRemoveField(field.id)
                                            }
                                            onChange={(_field) =>
                                                handleChange(field.id, _field)
                                            }
                                        />
                                    ))}
                                </Box>
                            </>
                        )}
                        <AcxButton
                            startIcon={<AddIcon />}
                            color="white"
                            onClick={handleAddField}
                            sx={{
                                border: `1px solid ${theme.palette.lightgrayBorder.main} `,
                                background: theme.palette.white.main,
                                color: theme.palette.blue.main,
                            }}
                        >
                            Add Field
                        </AcxButton>
                    </Box>
                </DataSourceContainer>
                <Container maxWidth="xl">
                    <Box
                        sx={{
                            display: "flex",
                            gap: theme.spacing(3),
                        }}
                    >
                        <Typography
                            sx={{
                                fontWeight: 600,
                                fontSize: "14px",
                                color: theme.palette.gray[500],
                            }}
                        >
                            Advanced Options:
                        </Typography>
                        <FormGroup
                            sx={{
                                gap: theme.spacing(3),
                                flexDirection: "row",
                                ".MuiSwitch-switchBase.Mui-checked": {
                                    // reduce translate since switch is slimmer
                                    transform: "translateX(12px)",
                                },
                            }}
                        >
                            <FormControlLabel
                                sx={{ margin: 0 }}
                                control={
                                    <Switch
                                        sx={{
                                            margin: theme.spacing(0, 1, 0, 0),
                                        }}
                                        checked={shouldApplyConditions}
                                        onChange={() =>
                                            setShouldApplyConditions(
                                                !shouldApplyConditions,
                                            )
                                        }
                                    />
                                }
                                label={
                                    <Typography
                                        sx={{
                                            fontSize: "14px",
                                            color: theme.palette.gray[800],
                                        }}
                                    >
                                        Apply Conditions to Extraction
                                    </Typography>
                                }
                            />
                            <FormControlLabel
                                sx={{ margin: 0 }}
                                control={
                                    <Switch
                                        sx={{
                                            margin: theme.spacing(0, 1, 0, 0),
                                        }}
                                        checked={shouldConcatenateFields}
                                        onChange={() =>
                                            setShouldConcatenateFields(
                                                !shouldConcatenateFields,
                                            )
                                        }
                                    />
                                }
                                label={
                                    <Typography
                                        sx={{
                                            fontSize: "14px",
                                            color: theme.palette.gray[800],
                                        }}
                                    >
                                        Concatenate Fields
                                    </Typography>
                                }
                            />
                        </FormGroup>
                    </Box>
                </Container>
                {shouldApplyConditions && <ConditionalLogic store={store} />}
                {shouldConcatenateFields && <ConcatFields store={store} />}
            </>
        );
    },
);

export default SourceConfiguration;
